import * as mapper from '@/mappers/call.mapper';

import apiClient from '@/lib/api-client';
import { raiseAxiosErrorOrDefault } from '@/lib/error';
import { buildQueryParams } from '@/lib/utils';

import type {
  Call,
  CallDetailResponse,
  CallFilterParams,
  CallTranscript,
  ConnectionDetails,
  DispatchAgentRequest,
} from '@/types/call.type';
import type { PaginatedResponse } from '@/types/common.type';

export const fetchAllCalls = async (params: CallFilterParams): Promise<PaginatedResponse<Call>> => {
  try {
    const response = await apiClient.get<PaginatedResponse<Call>>('/calls', null, {
      params: buildQueryParams(params, {
        ...(params.filters && {
          call_status: params.filters.status,
          call_mode: params.filters.mode,
        }),
      }),
    });
    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to fetch calls');
  }
};

/**
 * Make an outbound call to a phone number and dispatch agent to the call
 * @param payload
 */
export const dispatchAgentWeb = async (
  payload: DispatchAgentRequest
): Promise<ConnectionDetails> => {
  try {
    const response = await apiClient.post<ConnectionDetails>(
      '/test-call/web',
      mapper.mapDispatchAgentRequest(payload),
      mapper.mapDispatchAgentResponse
    );

    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Error fetching connection details!');
  }
};

/**
 * Dispatch an agent to a room and get connection details
 * @param payload
 * @returns Connection details
 */
export const dispatchAgentPhone = async (payload: DispatchAgentRequest): Promise<void> => {
  try {
    await apiClient.post('/test-call/phone', mapper.mapDispatchAgentPhoneRequest(payload));
  } catch (error) {
    raiseAxiosErrorOrDefault(error, 'Error dispatching agent to phone call!');
  }
};

/**
 * Fetch call details by ID
 * @param callId
 * @returns Call details by ID
 */
export const fetchCallById = async (callId: number): Promise<CallDetailResponse> => {
  try {
    const response = await apiClient.get<CallDetailResponse>(
      `/call/${callId}`,
      mapper.mapCallDetailResponse
    );
    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to fetch call details');
  }
};

/**
 * Fetch call transcripts
 * @param callId
 * @returns Call transcripts
 */
export const fetchCallTranscripts = async (callId: number): Promise<CallTranscript[]> => {
  try {
    const response = await apiClient.get<CallTranscript[]>(
      `/call/${callId}/transcript`,
      mapper.mapTranscriptResponse
    );
    return response;
  } catch {
    return [];
  }
};

/**
 * Fetch call recording URL by ID
 * @param callId
 * @returns Call recording URL
 */
export const fetchCallRecording = async (callId: number): Promise<string | null> => {
  try {
    const response = await apiClient.get<{ data: string | null }>(`/call/${callId}/recording`);
    return response.data;
  } catch {
    return null;
  }
};
